import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SectionHero from '../../components/sections/SectionHero';
import SectionTrustBar from '../../components/sections/SectionTrustBar';
import SectionServices from '../../components/sections/SectionServices';
import SectionMidCTA from '../../components/sections/SectionMidCTA';
import SectionAIBlock from '../../components/sections/SectionAIBlock';
import SectionProcess from '../../components/sections/SectionProcess';
import SectionWhyUs from '../../components/sections/SectionWhyUs';
import SectionAreas from '../../components/sections/SectionAreas';
import SectionTestimonials from '../../components/sections/SectionTestimonials';
import SectionFAQ from '../../components/sections/SectionFAQ';
import SectionFinalCTA from '../../components/sections/SectionFinalCTA';

const capabilities = [
  {
    icon: 'ri-award-line',
    title: 'Multi-Agency Accreditation',
    description:
      'Certified by PADI, SSI, SDI, TDI, and ERDI — giving our divers credentials that are recognized by every major diving organization on the planet.',
    image:
      'https://public.readdy.ai/ai/img_res/77153eebbc6959514b9df94aac8db206.jpg',
  },
  {
    icon: 'ri-anchor-line',
    title: 'Waterfront Dive Boat Operations',
    description:
      'Our base at Pier 5 on Biscayne Blvd gives every student real boat-based experience in authentic ocean conditions from day one — not a quarry.',
    image:
      'https://readdy.ai/api/search-image?query=professional%20dive%20boat%20Miami%20waterfront%20Biscayne%20Bay%20pier%20marina%20divers%20preparing%20equipment%20professional%20dive%20operation%20boat%20ready%20Florida%20ocean%20beautiful%20day&width=600&height=400&seq=about-cap-2-v3&orientation=landscape',
  },
  {
    icon: 'ri-book-open-line',
    title: 'Complete Training Curriculum',
    description:
      'From Open Water through Divemaster, technical diving, rescue, and 15+ specialties — the most complete dive training program in South Florida.',
    image:
      'https://readdy.ai/api/search-image?query=professional%20scuba%20diving%20training%20curriculum%20structured%20learning%20group%20instructor%20classroom%20theory%20professional%20dive%20school%20education%20Miami&width=600&height=400&seq=about-cap-3-v3&orientation=landscape',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Public Safety & ERDI Programs',
    description:
      'As an ERDI certified center, we provide specialized training for first responders, public safety dive teams, and emergency response professionals in Miami.',
    image:
      'https://readdy.ai/api/search-image?query=public%20safety%20diving%20emergency%20response%20first%20responder%20dive%20team%20training%20professional%20underwater%20search%20rescue%20ERDI%20specialized%20training%20Miami%20law%20enforcement&width=600&height=400&seq=about-cap-4-v3&orientation=landscape',
  },
];

const steps = [
  {
    number: '01',
    title: 'Our Standard',
    description: 'We set the bar high. Every instructor is selected for technical expertise, teaching ability, and absolute commitment to diver safety.',
    icon: 'ri-focus-3-line',
    detail: 'Instructors are evaluated not just on credentials — but on how they teach.',
  },
  {
    number: '02',
    title: 'Our Method',
    description: 'Structured, progressive, and never rushed. Each skill is taught, practiced, and confirmed before any student advances.',
    icon: 'ri-book-2-line',
    detail: 'We produce divers who are genuinely capable — not just cardholders.',
  },
  {
    number: '03',
    title: 'Our Environment',
    description: 'Real boats. Real ocean. Real conditions. We don\'t just train you for the pool — we prepare you for the sea.',
    icon: 'ri-anchor-line',
    detail: 'Pier 5, Biscayne Bay puts you in the water from the very first dive.',
  },
  {
    number: '04',
    title: 'Our Commitment',
    description: 'Post-certification support, advanced programs, and a dive community that supports your growth for life.',
    icon: 'ri-heart-line',
    detail: 'Most of our divers come back — for advanced training, dive trips, and community.',
  },
];

const differentiators = [
  {
    icon: 'ri-shield-check-line',
    title: 'Founded on Safety, Not Speed',
    description: 'Our philosophy has always been to produce divers who are genuinely safe. Every program, every class, every dive reflects this standard.',
  },
  {
    icon: 'ri-anchor-line',
    title: 'Miami\'s Premier Waterfront Dive Center',
    description: 'Pier 5, Biscayne Blvd is one of Miami\'s most exceptional diving locations. Our students dive in real ocean environments from day one.',
  },
  {
    icon: 'ri-team-line',
    title: 'Instructors with Real Depth',
    description: 'Our team has accumulated thousands of dives across tropical reefs, technical wrecks, and open ocean environments worldwide.',
  },
  {
    icon: 'ri-global-line',
    title: 'Internationally Recognized Credentials',
    description: 'Whether you want to dive in the Maldives, the Red Sea, or right here in Miami — your Ace Diving certification is respected everywhere.',
  },
  {
    icon: 'ri-user-star-line',
    title: 'A Community, Not Just a Course',
    description: 'Many of our divers return again and again — for advanced training, dive trips, and to be part of a community that shares their passion.',
  },
];

const areas = [
  'Miami', 'Miami Beach', 'Brickell', 'Coconut Grove', 'Coral Gables',
  'Key Largo', 'Key West', 'Biscayne Bay', 'Biscayne National Park',
  'Fort Lauderdale', 'South Beach', 'Hallandale',
];

const testimonials = [
  {
    name: 'Carlos V.',
    role: 'Technical Diver — Miami',
    rating: 5,
    quote:
      'I\'ve trained at dive centers around the world — in the Caribbean, Southeast Asia, and Europe. Ace Diving Miami matches or exceeds the very best of them. The instructors here aren\'t just certified — they are genuinely exceptional teachers who invest in every student they work with.',
    avatar:
      'https://readdy.ai/api/search-image?query=confident%20Latino%20professional%20man%20portrait%20headshot%20casual%20natural%20light%20outdoor%20friendly%20warm%20expression&width=100&height=100&seq=avatar-about-1-v3&orientation=squarish',
  },
  {
    name: 'Priya N.',
    role: 'Open Water to Divemaster — Miami',
    rating: 5,
    quote:
      'I started with zero diving experience and am now a certified Divemaster. Every step of that journey happened with Ace Diving. The quality of instruction, the care for students, and the community they\'ve built are genuinely irreplaceable.',
    avatar:
      'https://readdy.ai/api/search-image?query=professional%20woman%20smiling%20portrait%20headshot%20natural%20background%20warm%20light%20friendly%20casual%20confident&width=100&height=100&seq=avatar-about-2-v3&orientation=squarish',
  },
  {
    name: 'Tyler J.',
    role: 'Rescue Diver & Private Charter — South Beach',
    rating: 5,
    quote:
      'What sets Ace Diving apart is the team. Technically excellent, genuinely passionate about diving, and honestly invested in their students. This isn\'t a volume operation — every person who trains here gets real, personalized instruction.',
    avatar:
      'https://readdy.ai/api/search-image?query=young%20professional%20man%20outdoor%20portrait%20headshot%20smiling%20confident%20natural%20warm%20light%20casual%20friendly&width=100&height=100&seq=avatar-about-3-v3&orientation=squarish',
  },
];

const faqs = [
  {
    question: 'How long has Ace Diving been operating in Miami?',
    answer:
      'Ace Diving has been one of Miami\'s leading dive centers for over 15 years, operating from their waterfront base at Pier 5, 401 Biscayne Blvd. They have certified thousands of divers across South Florida and are recognized as a premier PADI, SSI, SDI, TDI, and ERDI training center.',
  },
  {
    question: 'What makes Ace Diving different from other dive schools in Miami?',
    answer:
      'Ace Diving stands apart through multi-agency accreditation, a real boat-based training operation on Biscayne Bay, small class sizes, and a philosophy that prioritizes genuine diver readiness over fast certification. Their instructor team brings decades of combined professional diving experience.',
  },
  {
    question: 'Does Ace Diving offer group bookings or corporate dive experiences?',
    answer:
      'Yes. Ace Diving offers group certification programs, private charters, corporate team experiences, and snorkeling excursions. Contact the team at +1 786-241-3194 or info@ace-diving.com to discuss group rates and available dates.',
  },
  {
    question: 'What is ERDI diving and does Ace Diving offer it?',
    answer:
      'ERDI (Emergency Response Diving International) is the public safety diving division of TDI. Ace Diving is an ERDI-certified center offering specialized training for law enforcement, fire departments, military, and first response dive teams in Miami and South Florida.',
  },
  {
    question: 'How do I contact Ace Diving Miami?',
    answer:
      'Reach Ace Diving Miami at +1 786-241-3194, by email at info@ace-diving.com, or visit their waterfront location at 401 Biscayne Blvd, Pier 5, Slip 33, Miami, FL 33132. WhatsApp is also available at the same number for quick responses.',
  },
];

export default function About() {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Navbar />
      <main>
        <SectionHero
          eyebrow="About Ace Diving Miami"
          headline={
            <>
              Built on a Higher<br />
              Standard of<br />
              <span className="text-[#00B4D8]">Diving Excellence.</span>
            </>
          }
          subheadline="For over 15 years, Ace Diving Miami has set the benchmark for professional scuba training from their waterfront home at Pier 5, Biscayne Blvd — serving thousands of divers who demand more than an average certification."
          cta1={{ text: 'Train With Our Team', href: '#contact' }}
          cta2={{ text: 'Our Story', href: '#why-us' }}
          stats={[
            { value: '15+', label: 'Years in Miami' },
            { value: '2,400+', label: 'Divers Trained' },
            { value: '5', label: 'Cert Agencies' },
            { value: 'Pier 5', label: 'Biscayne Blvd' },
          ]}
          backgroundImage="https://readdy.ai/api/search-image?query=professional%20scuba%20diving%20instructor%20experienced%20expert%20Miami%20ocean%20boat%20waterfront%20Biscayne%20Bay%20dive%20team%20professional%20instructors%20cinematic%20portrait%20documentary%20photography&width=1920&height=1080&seq=hero-about-v3&orientation=landscape"
          backgroundAlt="Ace Diving Miami — professional dive instruction team"
        />
        <SectionTrustBar />
        <SectionServices
          eyebrow="Our Capabilities"
          headline="What Ace Diving Miami Brings to Every Program"
          services={capabilities}
          ctaText="Explore Our Full Services"
        />
        <SectionMidCTA
          headline="Ready to Train With Miami's Best?"
          subheadline="Reach out for a no-obligation conversation about your diving goals. We'll recommend the right path forward."
          cta1={{ text: 'Start Training', href: '#contact' }}
          cta2={{ text: '+1 786-241-3194', href: 'tel:+17862413194' }}
        />
        <SectionAIBlock
          eyebrow="Who We Are"
          headline="About Ace Diving Miami"
          lead="Ace Diving Miami is a multi-agency certified scuba dive center based at Pier 5, 401 Biscayne Blvd, Miami — providing professional scuba training, guided dives, private charters, and technical diving programs to thousands of divers across South Florida."
          paragraphs={[
            'Accredited by PADI, SSI, SDI, TDI, and ERDI, Ace Diving brings the full spectrum of recreational, technical, and public safety diving instruction under one roof. Their waterfront location on Biscayne Bay provides an authentic marine training environment from the very first session.',
            'The Ace Diving philosophy is simple and unwavering: produce divers who are genuinely safe, skilled, and prepared — not just certified. Every instructor is selected for technical expertise, teaching ability, and real passion for the underwater world.',
          ]}
          tags={[
            'PADI Certified Center',
            'SSI Dive Center',
            'SDI / TDI / ERDI',
            'Biscayne Bay',
            'Pier 5 Miami',
            'Technical Diving',
            'Public Safety Diving',
            'Private Charters',
          ]}
        />
        <SectionProcess
          eyebrow="Our Philosophy"
          headline="The Ace Diving Approach to Training Excellence"
          steps={steps}
          ctaText="Train With Our Team"
        />
        <SectionWhyUs
          eyebrow="Our Differentiators"
          headline="What Sets Ace Diving Apart in Miami"
          reasons={differentiators}
          image="https://readdy.ai/api/search-image?query=dive%20team%20professional%20scuba%20instructors%20boat%20Miami%20ocean%20Biscayne%20Bay%20waterfront%20pier%20professional%20dive%20boat%20operation%20expert%20team%20preparation%20cinematic%20portrait&width=700&height=900&seq=why-about-v3&orientation=portrait"
          imageAlt="Ace Diving Miami team — professional instructors"
          ctaText="Train With Our Team"
          badge1={{ label: 'Miami\'s Premier', sublabel: 'Dive Center Since 2009' }}
          badge2={{ value: '5', label: 'Cert Agencies' }}
          extraParagraph="Ace Diving was built on one principle: if you train here, you leave genuinely ready. That standard has defined every program, every instructor hire, and every dive we\'ve led across more than 15 years in Miami."
        />
        <SectionAreas
          eyebrow="Where We Operate"
          headline="Diving Miami and All of South Florida"
          description="From Biscayne Bay to the Florida Keys, our instructors know every reef, wreck, and drift dive in the region. We serve divers from across Miami and South Florida who want training and experiences they can trust."
          areas={areas}
          ctaText="Book a Program"
        />
        <SectionTestimonials
          eyebrow="Our Community"
          headline="What the Ace Diving Community Says"
          testimonials={testimonials}
          ctaText="Join Our Community"
        />
        <SectionFAQ
          eyebrow="About Our Center"
          headline="Common Questions About Ace Diving Miami"
          faqs={faqs}
        />
        <SectionFinalCTA
          eyebrow="Start Training"
          headline="Ready to Train With Miami's Most Trusted Dive Team?"
          subheadline="Whether you're a complete beginner or an experienced diver looking to advance — we'll build a program around your exact goals, schedule, and budget."
          ctaText="Train With Our Team"
        />
      </main>
      <Footer />
    </div>
  );
}
